import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Zap, FileText, ShoppingCart, Receipt, CheckCircle, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

export default function AutoJournalPage() {
  const { t } = useLanguage();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await api.get(`/accounting/auto-journal/preview?${params}`);
      setPreview(res.data);
    } catch { toast.error('Failed to load preview'); }
    setLoading(false);
  };

  useEffect(() => { fetchPreview(); }, []);

  const generate = async (sourceTypes) => {
    setGenerating(true);
    setResult(null);
    try {
      const body = { source_types: sourceTypes };
      if (dateFrom) body.date_from = dateFrom;
      if (dateTo) body.date_to = dateTo;
      const res = await api.post('/accounting/auto-journal/generate', body);
      setResult(res.data);
      toast.success(`Created ${res.data.entries_created} journal entries`);
      fetchPreview();
    } catch { toast.error('Failed to generate entries'); }
    setGenerating(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="auto-journal-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center shadow-lg shadow-amber-200/50">
              <Zap size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Auto Journal Entries</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Auto-generate from sales and expense transactions</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchPreview}><RefreshCw size={14} className="mr-1" /> Refresh</Button>
        </div>

        {/* Date Range Selector */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">From Date</label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-44" data-testid="date-from" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">To Date</label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-44" data-testid="date-to" />
              </div>
              <Button variant="outline" size="sm" onClick={fetchPreview}>Preview</Button>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]"><div className="w-8 h-8 border-4 border-warning border-t-transparent rounded-full animate-spin" /></div>
        ) : preview && (
          <>
            {/* Preview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-success/30 bg-success/10">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-success/20 flex items-center justify-center"><ShoppingCart size={20} className="text-success" /></div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Sales Transactions</p>
                      <p className="text-2xl font-bold text-foreground" data-testid="sales-total">{preview.sales.total}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Already journalized: {preview.sales.already_journalized}</span>
                    <Badge className="bg-success/20 text-success text-[10px]">{preview.sales.pending} pending</Badge>
                  </div>
                  <Button className="w-full mt-3" size="sm" onClick={() => generate(['sales'])} disabled={generating || preview.sales.pending === 0} data-testid="generate-sales-btn">
                    {generating ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Zap size={14} className="mr-1" />}
                    Generate Sales Entries
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-error/30 bg-error/10">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-error/20 flex items-center justify-center"><Receipt size={20} className="text-error" /></div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Expense Transactions</p>
                      <p className="text-2xl font-bold text-foreground" data-testid="expenses-total">{preview.expenses.total}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Already journalized: {preview.expenses.already_journalized}</span>
                    <Badge className="bg-error/20 text-error text-[10px]">{preview.expenses.pending} pending</Badge>
                  </div>
                  <Button className="w-full mt-3" size="sm" variant="destructive" onClick={() => generate(['expenses'])} disabled={generating || preview.expenses.pending === 0} data-testid="generate-expenses-btn">
                    {generating ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Zap size={14} className="mr-1" />}
                    Generate Expense Entries
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-warning/30 bg-warning/10">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-warning/20 flex items-center justify-center"><FileText size={20} className="text-warning" /></div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Total Pending</p>
                      <p className="text-2xl font-bold text-foreground" data-testid="total-pending">{preview.total_pending}</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Generate all pending entries at once</p>
                  <Button className="w-full" size="sm" variant="outline" onClick={() => generate(['sales', 'expenses'])} disabled={generating || preview.total_pending === 0} data-testid="generate-all-btn">
                    {generating ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Zap size={14} className="mr-1" />}
                    Generate All
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Result */}
            {result && (
              <Card className="border-success/30 bg-success/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-success">
                    <CheckCircle size={16} /> Generation Complete
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm mb-3" data-testid="result-count">Created <strong>{result.entries_created}</strong> journal entries for period {result.date_range?.from} to {result.date_range?.to}</p>
                  {result.entries?.length > 0 && (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {result.entries.map(e => (
                        <div key={e.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-border text-sm">
                          <div className="flex items-center gap-2">
                            <Badge className={`text-[10px] ${e.source_type === 'sale' ? 'bg-success/20 text-success' : 'bg-error/20 text-error'}`}>
                              {e.source_type === 'sale' ? 'Sale' : 'Expense'}
                            </Badge>
                            <span className="font-mono text-xs text-muted-foreground">{e.entry_number}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-muted-foreground text-xs truncate max-w-[200px]">{e.description}</span>
                            <span className="font-medium text-foreground">{e.total_debit?.toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Info */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle size={18} className="text-info mt-0.5 shrink-0" />
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><strong>How it works:</strong> Auto Journal analyzes your sales and expense records and creates double-entry journal entries automatically.</p>
                    <p>Sales are mapped to Cash/Bank (debit) and Sales Revenue (credit). Expenses are mapped to the appropriate expense account (debit) and Cash/Bank (credit).</p>
                    <p>Entries that have already been journalized are skipped to prevent duplicates.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
